import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import { useRole } from '../../contexts/RoleContext';
import apiService from '../../services/api';
import Card, { CardHeader, CardTitle, CardContent } from '../../components/Card';
import { AdminPageLayout, StatCard, StatGrid, EmptyState, Grid, Section, Badge, Col } from '../../components';
import {
  Listing,
  ListingCard
} from '../../components/listing/v2';
import {
  Building2,
  Globe,
  Layers,
  ShieldCheck,
  Activity,
  PlusCircle,
  ClipboardList,
  GraduationCap,
  Users,
  CheckCircle
} from 'lucide-react';

const PlatformDashboard = () => {
  const { user_name, user_email, isPlatformAdmin } = useRole();
  const [stats, setStats] = useState({
    totalClubs: 0,
    activeClubs: 0,
    inactiveClubs: 0,
    totalUsers: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
    loadInboxTasks();
    // eslint-disable-next-line
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const clubsData = await apiService.getClubs({ limit: 100 });
      const clubs = clubsData.clubs || [];
      const activeClubs = clubs.filter(club => club.status === 'active').length;
      const inactiveClubs = clubs.filter(club => club.status === 'inactive').length;
      setStats({
        totalClubs: clubs.length,
        activeClubs,
        inactiveClubs,
        totalUsers: 0, // TODO: Add endpoint for total users across platform
        recentActivity: clubs.slice(0, 5)
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadInboxTasks = async () => {
    try {
      setTasksLoading(true);
      const response = await apiService.getInboxTasks();
      setTasks(response.tasks?.filter(task => task.role === 'platform_admin') || []);
    } catch (err) {
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  const QUICK_ACTIONS = [
    {
      id: 'manage-clubs',
      title: 'Manage Clubs',
      description: 'View, edit, and monitor all clubs on the platform',
      icon: <Globe size={40} />,
      link: '/admin/clubs',
      variant: 'default'
    },
    {
      id: 'system-templates',
      title: 'System Templates',
      description: 'Manage roles, skills, documents, calendars, and training objective templates',
      icon: <Layers size={40} />,
      link: '/admin/system-templates',
      variant: 'default'
    },
    {
      id: 'platform-admins',
      title: 'Platform Admins',
      description: 'Manage platform administrator users and permissions',
      icon: <ShieldCheck size={40} />,
      link: '/admin/platform-admins',
      variant: 'default',
      disabled: true
    },
    {
      id: 'system-monitor',
      title: 'System Monitor',
      description: 'Platform health, performance metrics, and system settings',
      icon: <Activity size={40} />,
      link: '/admin/system',
      variant: 'default',
      disabled: true
    }
  ];

  if (!isPlatformAdmin) {
    return <div className="access-denied">Access denied. Platform admin role required.</div>;
  }

  return (
    <AdminPageLayout
      title="PLATFORM DASHBOARD"
      subtitle="Comprehensive overview of platform operations"
      backButton={false}
      maxWidth="1400px"
    >
      {loading ? (
        <div className="loading" style={{ textAlign: 'center', padding: '4rem' }}>Loading dashboard data...</div>
      ) : error ? (
        <div className="error-message" style={{ background: 'var(--color-error-background)', color: 'var(--color-error)', padding: '2rem', borderRadius: '12px', textAlign: 'center' }}>
          <p>Error loading dashboard: {error}</p>
          <Button onClick={loadDashboardData} variant="outline" style={{ marginTop: '1rem' }}>Retry</Button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>

          {/* 1. Statistics Summary */}
          <Section title="Ecosystem Pulse">
            <StatGrid>
              <StatCard
                number={stats.totalClubs}
                label="Total Entities"
                detail={`${stats.activeClubs} Operational • ${stats.inactiveClubs} Suspended`}
                icon={<Building2 size={24} />}
              />
              <StatCard
                number={stats.totalUsers}
                label="Active Identities"
                detail="Across all registered organizations"
                icon={<Users size={24} />}
                variant="primary"
              />
              <StatCard
                number={stats.activeClubs}
                label="Strategic Growth"
                detail="Net operational nodes"
                icon={<Activity size={24} />}
                variant="success"
              />
            </StatGrid>
          </Section>

          {/* 2. Quick Actions - Surface Level 1 */}
          <Section title="Command Center">
            <Listing
              items={QUICK_ACTIONS}
              variant="grid"
              renderItem={(action) => (
                <div 
                  key={action.id}
                  style={{ 
                    position: 'relative',
                    filter: action.disabled ? 'grayscale(100%)' : 'none'
                  }}
                >
                  <ListingCard
                    title={action.title}
                    subtitle={action.description}
                    icon={action.icon}
                    level={1}
                    iconVariant="admin"
                    onClick={action.disabled ? undefined : () => navigate(action.link)}
                    style={action.disabled ? {
                      opacity: 0.35,
                      cursor: 'not-allowed',
                      pointerEvents: 'none'
                    } : undefined}
                  />
                  {action.disabled && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      padding: '6px 12px',
                      background: 'rgba(255, 165, 0, 0.9)',
                      color: '#000',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      borderRadius: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                      zIndex: 10
                    }}>
                      Coming Soon
                    </div>
                  )}
                </div>
              )}
            />
          </Section>

          {/* 3. Operational Intel - Surface Level 1 */}
          <Section title="Operational Intelligence">
            <Grid columns={12} gap="2rem">
              {/* Pending Actions */}
              <Col span={5}>
                <Card level={1} height="100%">
                  <CardHeader>
                    <CardTitle as="h3" style={{ fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: '0.75rem', fontWeight: 700 }}>
                      <ClipboardList size={20} style={{ color: 'var(--color-primary)' }} /> PENDING OPERATIONS
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {tasksLoading ? (
                      <div style={{ opacity: 0.6, padding: '1rem' }}>Synchronizing task queue...</div>
                    ) : tasks.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '3rem', opacity: 0.6 }}>
                        <CheckCircle size={32} style={{ marginBottom: '1rem', display: 'block', margin: '0 auto 1rem', color: 'var(--color-success)' }} />
                        <div style={{ fontSize: '0.95rem', fontWeight: 500 }}>Ecosystem is optimal</div>
                        <div style={{ fontSize: '0.85rem' }}>No pending administrative actions</div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {tasks.map(task => (
                          <Card key={task.id} level={2} padding="1rem" hover={true} onClick={() => navigate(task.link)}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>{task.title}</div>
                                <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem', lineHeight: '1.4' }}>{task.description}</div>
                              </div>
                              <Button size="xs" variant="ghost" style={{ marginLeft: '1rem' }}>Execute</Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Col>

              {/* Activity Log */}
              <Col span={7}>
                <Card level={1} height="100%">
                  <CardHeader>
                    <CardTitle as="h3" style={{ fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: '0.75rem', fontWeight: 700 }}>
                      <Activity size={20} style={{ color: 'var(--color-primary)' }} /> SYSTEM ACTIVITY LOG
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats.recentActivity.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {stats.recentActivity.map((club) => (
                          <div key={club.id} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '1rem',
                            background: 'var(--surface-lvl2)',
                            borderRadius: '12px',
                            border: '1px solid var(--color-border-light)',
                            transition: 'all 0.2s ease'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                              <div style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                background: club.status === 'active' ? 'var(--color-success)' : 'var(--color-text-tertiary)',
                                boxShadow: club.status === 'active' ? '0 0 10px var(--color-success-alpha)' : 'none'
                              }} />
                              <div>
                                <div style={{ fontWeight: '600', fontSize: '1rem' }}>{club.name}</div>
                                <div style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.2rem' }}>
                                  {club.club_type?.toUpperCase()} • Registered {new Date(club.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                              {club.total_players > 0 && (
                                <Badge variant="outline" size="sm">{club.total_players} MEMBERS</Badge>
                              )}
                              <Button to={`/admin/clubs/${club.id}`} variant="outline" size="xs">Inspect</Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyState
                        icon={<Building2 size={40} />}
                        title="No Operational Nodes"
                        description="Initialise your first organization to generate activity telemetry."
                      />
                    )}
                  </CardContent>
                </Card>
              </Col>
            </Grid>
          </Section>

          {/* 5. Platform Architecture Overview - Multi-level Depth */}
          <Section title="Platform Intelligence">
            <Grid columns={3} gap="2rem">
              <Card level={1}>
                <CardHeader>
                  <CardTitle as="h4" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.05rem' }}>
                    <Layers size={18} /> Multitenancy
                  </CardTitle>
                </CardHeader>
                <CardContent style={{ fontSize: '0.9rem', opacity: 0.8, lineHeight: '1.6' }}>
                  Isolated data structures for every club organization, ensuring top-tier security and privacy standard across the pitch.
                </CardContent>
              </Card>
              <Card level={1}>
                <CardHeader>
                  <CardTitle as="h4" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.05rem' }}>
                    <ShieldCheck size={18} /> Access Control
                  </CardTitle>
                </CardHeader>
                <CardContent style={{ fontSize: '0.9rem', opacity: 0.8, lineHeight: '1.6' }}>
                  Granular permission sets from Platform Admin down to Parents, maintaining clear lines of authority and data visibility.
                </CardContent>
              </Card>
              <Card level={1}>
                <CardHeader>
                  <CardTitle as="h4" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.05rem' }}>
                    <Users size={18} /> User Lifecycle
                  </CardTitle>
                </CardHeader>
                <CardContent style={{ fontSize: '0.9rem', opacity: 0.8, lineHeight: '1.6' }}>
                  Automatic stakeholder provisioning during club onboarding, reducing manual setup overhead for club administrators.
                </CardContent>
              </Card>
            </Grid>
          </Section>
        </div>
      )}
    </AdminPageLayout>
  );
};

export default PlatformDashboard;